'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["relAttr", "onLinkTypeChange", "altText", "loadingValue", "onUpdate", "onSave", "onClose", "imageFileInfo", "onReplace", "showRefAndTargetControls"];
import PropTypes from 'prop-types';
import { useState } from 'react';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HR from 'UIComponents/elements/HR';
import UIFieldset from 'UIComponents/form/UIFieldset';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import UIFileAttachment from 'UIComponents/attachment/UIFileAttachment';
import UIImage from 'UIComponents/image/UIImage';
import { KeyTypes, PopoverType } from '../../constants/popover';
import { useLinkValidation } from '../../hooks/useLinkValidation';
import { isWhitespace } from '../../utils/url';
import { LinkTypes } from '../../constants/link';
import { LazyLoadingTypes } from '../../constants/shared';
import MorphingPopoverBody from '../MorphingPopoverBody';
import ExplicitSaveControls from '../ExplicitSaveControls';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import LinkAdvancedPopoverControls from '../links/LinkAdvancedPopoverControls';
import LinkPopoverControlsV2 from '../links/LinkPopoverControlsV2';
import LazyLoadingInput from '../LazyLoadingInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nNameSpace = `RichTextLib`;
const i18nKey = suffix => `${i18nNameSpace}.image.${suffix}`;
const linkI18nKey = suffix => `${i18nNameSpace}.links.advanced.${suffix}`;
const ImageFileAttachment = styled(UIFileAttachment).withConfig({
  displayName: "ImagePopoverBodyEdit__ImageFileAttachment",
  componentId: "sc-19yqpq0-0"
})(["width:100%;"]);
const StyledImage = styled(UIImage).withConfig({
  displayName: "ImagePopoverBodyEdit__StyledImage",
  componentId: "sc-19yqpq0-1"
})(["font-size:0;"]);
const getRenderImageAdvancedControls = ({
  relAttr,
  onRelAttrChange,
  linkType,
  loadingValue,
  onLoadingValueChange,
  dropdownClassName,
  tooltipClassName,
  isSubmitDisabled,
  onSubmit,
  onCancel
}) => ({
  onCloseMorphedContent
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LinkAdvancedPopoverControls, {
      className: "p-x-5 p-bottom-1",
      relAttr: relAttr,
      onRelAttrChange: onRelAttrChange,
      disabled: linkType === LinkTypes.NONE,
      tooltipClassName: tooltipClassName
    }), /*#__PURE__*/_jsx(HR, {
      distance: "xs"
    }), /*#__PURE__*/_jsx(UIFieldset, {
      className: "p-x-5 p-bottom-5",
      _size: "small",
      children: /*#__PURE__*/_jsx(LazyLoadingInput, {
        tooltipClassName: tooltipClassName,
        dropdownClassName: dropdownClassName,
        onChange: onLoadingValueChange,
        value: loadingValue,
        label: "imageLabel"
      })
    }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
      className: "p-x-5 p-bottom-5",
      popoverType: PopoverType.EDIT,
      isSubmitDisabled: isSubmitDisabled,
      onSubmit: () => {
        const success = onSubmit();
        if (!success) {
          onCloseMorphedContent();
        }
      },
      onCancel: onCancel
    })]
  });
};
const renderImagePreview = ({
  altText,
  imageFileInfo,
  onReplace
}) => {
  const {
    name,
    size,
    src
  } = imageFileInfo;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
      primaryText: i18nKey('imagePreviewLabel'),
      linkText: i18nKey('replace'),
      onLinkClick: onReplace
    }), /*#__PURE__*/_jsx(ImageFileAttachment, {
      fileName: name,
      fileInfo: size ? I18n.formatSize(size) : undefined,
      renderPreview: () => /*#__PURE__*/_jsx(StyledImage, {
        alt: altText,
        shape: "thumbnail",
        src: src
      })
    })]
  });
};
const propTypes = Object.assign({}, LinkPopoverControlsV2.propTypes, {
  relAttr: PropTypes.array,
  altText: PropTypes.string,
  imageFileInfo: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
});
function ImagePopoverBodyEdit(props) {
  const {
      relAttr,
      onLinkTypeChange,
      altText: _altText,
      loadingValue: _loadingValue,
      onUpdate,
      onSave,
      onClose,
      imageFileInfo,
      onReplace,
      showRefAndTargetControls
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [altText, setAltText] = useState(_altText);
  const [loadingValue, setLoadingValue] = useState(_loadingValue);
  const [handleLinkTypeChange, handleUpdate, _handleSubmit] = useLinkValidation({
    linkType: props.linkType,
    href: props.href,
    isValidLink: props.isValidLink,
    onLinkTypeChange,
    onUpdate,
    onSubmit: onSave
  });
  const handleCloseOnEscape = evt => {
    if (evt.key === KeyTypes.ESCAPE) {
      onClose();
    }
  };
  const handleSubmit = () => {
    return _handleSubmit({
      altText,
      loadingValue: LazyLoadingTypes[loadingValue]
    });
  };
  const handleRelAttrChange = value => {
    handleUpdate({
      relAttr: value
    });
  };
  const handleAltTextChange = evt => {
    const {
      value
    } = evt.target;
    setAltText(value);
  };
  const handleLoadingValueChange = evt => {
    const {
      value
    } = evt.target;
    setLoadingValue(value);
  };
  const hrClassname = `m-top-${props.showRefAndTargetControls ? 2 : 4}`;
  const isSubmitDisabled = props.linkType !== LinkTypes.NONE && isWhitespace(props.href);
  let morphingPopoverProps = {};
  if (showRefAndTargetControls) {
    // Only show the advanced link controls for non-email contexts
    morphingPopoverProps = {
      morphButtonTitle: linkI18nKey('title'),
      morphedContentTitle: linkI18nKey('title'),
      renderMorphedContent: getRenderImageAdvancedControls({
        relAttr,
        onRelAttrChange: handleRelAttrChange,
        linkType: props.linkType,
        loadingValue,
        onLoadingValueChange: handleLoadingValueChange,
        dropdownClassName: props.dropdownClassName,
        tooltipClassName: props.tooltipClassName,
        isSubmitDisabled,
        onSubmit: handleSubmit,
        onCancel: onClose
      })
    };
  }
  return /*#__PURE__*/_jsx("div", {
    className: "small-popover-body",
    tabIndex: "-1",
    children: /*#__PURE__*/_jsx(MorphingPopoverBody, Object.assign({
      title: i18nKey('popoverTitle'),
      titleClassName: "p-x-5 p-top-5"
    }, morphingPopoverProps, {
      children: /*#__PURE__*/_jsxs("div", {
        onKeyDown: handleCloseOnEscape,
        children: [/*#__PURE__*/_jsx(LinkPopoverControlsV2, Object.assign({}, rest, {
          className: "p-x-5 p-bottom-1",
          onLinkTypeChange: handleLinkTypeChange,
          onUpdate: handleUpdate,
          onSubmit: handleSubmit,
          onClose: onClose,
          showRefAndTargetControls: showRefAndTargetControls
        })), /*#__PURE__*/_jsx(HR, {
          distance: "xs",
          className: hrClassname
        }), /*#__PURE__*/_jsxs(UIFieldset, {
          className: "p-x-5 p-bottom-2",
          _size: "small",
          children: [renderImagePreview({
            altText,
            imageFileInfo,
            onReplace
          }), /*#__PURE__*/_jsx(UIFormControl, {
            className: "",
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('altTextLabel')
            }),
            tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('altTextHelp')
            }),
            tooltipPlacement: "top right",
            tooltipProps: {
              innerClassName: props.tooltipClassName
            },
            children: /*#__PURE__*/_jsx(UITextInput, {
              autoFocus: true,
              name: "alt-text",
              value: altText,
              onChange: handleAltTextChange
            })
          })]
        }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
          className: "m-top-3 p-x-5 p-bottom-5",
          popoverType: PopoverType.EDIT,
          isSubmitDisabled: isSubmitDisabled,
          onSubmit: handleSubmit,
          onCancel: onClose
        })]
      })
    }))
  });
}
ImagePopoverBodyEdit.propTypes = propTypes;
ImagePopoverBodyEdit.displayName = 'ImagePopoverBodyEdit';
export default ImagePopoverBodyEdit;